import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import TonyteBackground from '../components/TonyteBackground';
import { auth, DEFAULT_AVATAR, FIRESTORE_COLLECTIONS, firestore, functionsEu, httpsCallable, uploadFaceVerificationImage } from '../firebase';
import { useKeyboardInsets } from '../hooks/useKeyboardInsets';
import { useProfile } from '../hooks/useProfile';
import { Colors, Radius, Spacing, Typography } from '../theme';
import type { FaceVerificationStatus, RootStackParamList, StudentVerificationStatus } from '../types';
import { callSendVerificationEmail } from '../utils/studentVerification';
import VibeVerificationCameraModal, { type VibeVerificationCaptureResult } from '../features/verification/VibeVerificationCameraModal';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'VerificationCenter'>;

type StatusUi = {
  labelKey: 'verification.verified' | 'verification.pending' | 'verification.notVerified' | 'verification.failed';
  tone: 'success' | 'pending' | 'neutral' | 'failed';
};

type FaceCapture = {
  uri: string;
  fileName?: string;
  fileSize?: number;
  width?: number;
  height?: number;
};

const statusToneColor = {
  success: Colors.turquoise,
  pending: '#7dd3fc',
  neutral: Colors.textMuted,
  failed: Colors.sunset,
};

const isStudentVerified = (status?: StudentVerificationStatus, studentVerified?: boolean, isStudentVerifiedFlag?: boolean) =>
  status === 'verified' && (studentVerified === true || isStudentVerifiedFlag === true);

const getStudentStatusUi = (
  status?: StudentVerificationStatus,
  studentVerified?: boolean,
  isStudentVerifiedFlag?: boolean,
): StatusUi => {
  if (isStudentVerified(status, studentVerified, isStudentVerifiedFlag) || status === 'approved') return { labelKey: 'verification.verified', tone: 'success' };
  if (status === 'pending' || status === 'pending_admin_review' || status === 'email_ownership_confirmed' || status === 'email_sent' || status === 'email_verification_sent') {
    return { labelKey: 'verification.pending', tone: 'pending' };
  }
  if (status === 'failed' || status === 'denied' || status === 'rejected' || status === 'expired') return { labelKey: 'verification.failed', tone: 'failed' };
  return { labelKey: 'verification.notVerified', tone: 'neutral' };
};

const getVibeStatusUi = (status?: FaceVerificationStatus, verified?: boolean, submitted?: boolean): StatusUi => {
  if (verified || status === 'approved') return { labelKey: 'verification.verified', tone: 'success' };
  if (status === 'pending' || submitted) return { labelKey: 'verification.pending', tone: 'pending' };
  if (status === 'rejected') return { labelKey: 'verification.failed', tone: 'failed' };
  return { labelKey: 'verification.notVerified', tone: 'neutral' };
};

// react-native-vision-camera's capturePhotoToFile() (native) returns a bare
// filesystem path with no URI scheme (e.g. "/data/user/0/.../IMG.jpg") —
// genuinely needs `file://` prepended to become a usable URI, and native's
// own image pipeline/upload path relies on that. The web camera modal's
// captureFinalPhoto() (useVibeCameraSession.web.ts) already returns a
// complete, ready-to-use `blob:https://…` URL from URL.createObjectURL() —
// prepending file:// to that produced a real production bug
// ("file://blob:https://tonyte-app.web.app/…", which browsers can't resolve
// as an image source at all, since file:// doesn't understand another URI
// scheme nested inside it). Any path that already carries a recognized URI
// scheme (blob:, data:, http(s):, or an already-prefixed file:) is used
// as-is; only a bare, scheme-less path — native only — gets file://
// prepended, so this one call site correctly serves both platforms without
// either needing to know about the other's URI shape.
const toFaceCaptureUri = (path: string): string => (/^[a-z][a-z0-9+.-]*:/i.test(path) ? path : `file://${path}`);

const captureLooksUsable = (capture: FaceCapture): boolean => {
  if (!capture.uri) return false;
  if (capture.width && capture.width < 420) return false;
  if (capture.height && capture.height < 420) return false;
  if (capture.fileSize && capture.fileSize < 18_000) return false;
  return true;
};

const VerificationCenterScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const uid = auth.currentUser?.uid ?? null;
  const { profile } = useProfile(uid);
  const [studentEmail, setStudentEmail] = useState('');
  const [studentMessage, setStudentMessage] = useState('');
  const [studentSaving, setStudentSaving] = useState(false);
  const [vibeSaving, setVibeSaving] = useState(false);
  const [vibeMessage, setVibeMessage] = useState('');
  const [vibeFailedCaptures, setVibeFailedCaptures] = useState<{ front: FaceCapture; left: FaceCapture; right: FaceCapture; isFallback: boolean } | null>(null);
  const [localVibeSubmitted, setLocalVibeSubmitted] = useState(false);
  const [poseGuideVisible, setPoseGuideVisible] = useState(false);
  const [liveCameraVisible, setLiveCameraVisible] = useState(false);
  // Keep the student email field (and its button) above the keyboard, the
  // same way the chat composer does: iOS pads via KeyboardAvoidingView;
  // Android pads by the real IME inset (useKeyboardInsets — under
  // edge-to-edge the window no longer resizes for the keyboard).
  const androidKeyboard = useKeyboardInsets();
  const scrollRef = useRef<ScrollView>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  // The student card is the last one, so "end" = email field + button.
  const revealEmailField = () => requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
  useEffect(() => {
    if (emailFocused && androidKeyboard.visible) revealEmailField();
  }, [androidKeyboard.height, androidKeyboard.visible, emailFocused]);
  useEffect(() => {
    if (Platform.OS !== 'ios' || !emailFocused) return undefined;
    const subscription = Keyboard.addListener('keyboardDidShow', revealEmailField);
    return () => subscription.remove();
  }, [emailFocused]);

  useEffect(() => {
    setStudentEmail(profile?.studentEmail || profile?.requestedStudentEmail || profile?.email || auth.currentUser?.email || '');
  }, [profile?.email, profile?.requestedStudentEmail, profile?.studentEmail]);

  const studentUi = useMemo(
    () => getStudentStatusUi(profile?.studentVerificationStatus, profile?.studentVerified, profile?.isStudentVerified),
    [profile?.isStudentVerified, profile?.studentVerificationStatus, profile?.studentVerified],
  );
  const vibeVerified = profile?.isVibeVerified === true || profile?.faceVerified === true;
  const vibeStatus = vibeVerified ? 'approved' : profile?.faceVerificationStatus;
  const vibeUi = useMemo(
    () => getVibeStatusUi(vibeStatus, vibeVerified, localVibeSubmitted),
    [localVibeSubmitted, vibeStatus, vibeVerified],
  );

  const sendStudentVerification = async () => {
    const nextEmail = studentEmail.trim().toLowerCase();
    if (!nextEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setStudentMessage(t('verification.validStudentEmail'));
      return;
    }
    setStudentSaving(true);
    setStudentMessage('');
    try {
      await callSendVerificationEmail(nextEmail);
      setStudentMessage(t('verification.emailSent'));
    } catch (error) {
      setStudentMessage(error instanceof Error ? error.message : t('verification.emailSendFailed'));
    } finally {
      setStudentSaving(false);
    }
  };

  const openLiveVerificationCamera = () => {
    if (!profile?.id) {
      Alert.alert(t('auth.signInRequired'), t('verification.signInSub'));
      return;
    }
    // Web now has a real implementation (VibeVerificationCameraModal.web.tsx,
    // getUserMedia + MediaPipe FaceLandmarker in place of VisionCamera + ML
    // Kit) — gate on actual browser camera-API support rather than
    // Platform.OS, so only a genuinely unsupported browser/context (no
    // mediaDevices at all, or a non-HTTPS origin) falls back to this message
    // instead of every web user.
    const webCameraUnsupported = Platform.OS === 'web'
      && (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia);
    if (webCameraUnsupported) {
      Alert.alert(t('verification.notAvailableWebTitle', 'Not available on web yet'), t('verification.notAvailableWebBody', 'Vibe Verification needs the Tonyte iOS or Android app for now.'));
      return;
    }
    setVibeMessage('');
    setLiveCameraVisible(true);
  };

  const handleVibeVerificationCaptured = ({ frontPath, leftPath, rightPath, isFallback }: VibeVerificationCaptureResult) => {
    setLiveCameraVisible(false);
    const front: FaceCapture = { uri: toFaceCaptureUri(frontPath) };
    const left: FaceCapture = { uri: toFaceCaptureUri(leftPath) };
    const right: FaceCapture = { uri: toFaceCaptureUri(rightPath) };
    if (![front, left, right].every(captureLooksUsable)) {
      setVibeMessage(t('verification.selfieTooSmall'));
      return;
    }
    // No manual review/retake step for a genuine three-pose auto-capture —
    // submission proceeds immediately once they're in hand. A fallback
    // submission (isFallback) is different: see submitVibeVerification.
    void submitVibeVerification(front, left, right, isFallback);
  };

  const submitVibeVerification = async (front: FaceCapture, left: FaceCapture, right: FaceCapture, isFallback: boolean) => {
    if (!profile?.id) return;
    setVibeSaving(true);
    setVibeFailedCaptures(null);
    setVibeMessage(t('verification.camera.uploading'));
    try {
      const [frontUpload, leftUpload, rightUpload] = await Promise.all([
        uploadFaceVerificationImage(profile.id, front.uri, 'front'),
        uploadFaceVerificationImage(profile.id, left.uri, 'left'),
        uploadFaceVerificationImage(profile.id, right.uri, 'right'),
      ]);
      const requestRef = await addDoc(collection(firestore, 'faceVerificationRequests'), {
        userId: profile.id,
        userName: profile.name || 'Tonyte User',
        userEmail: profile.email || auth.currentUser?.email || '',
        userPhoto: profile.photo || DEFAULT_AVATAR,
        frontImageUrl: frontUpload.downloadUrl,
        leftImageUrl: leftUpload.downloadUrl,
        rightImageUrl: rightUpload.downloadUrl,
        poseType: 'front_left_right',
        status: 'pending',
        verificationMethod: 'face_pose_live',
        createdAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        livenessChecks: {
          source: 'camera',
          galleryUploadAllowed: false,
          // A fallback submission never had a pose confirmed by the
          // on-device model at all (that's why it fell back) — it must go
          // to a human, not the automated grant below.
          manualReviewRequired: isFallback,
        },
      });
      await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, profile.id), {
        faceVerificationStatus: 'pending',
        faceVerificationSubmittedAt: serverTimestamp(),
        latestFaceVerificationRequestId: requestRef.id,
        isVibeVerified: false,
        faceVerified: false,
        verificationBadge: null,
      });

      if (isFallback) {
        // Leave the request as 'pending' for a human admin to review (the
        // same queue/Approve-Reject flow the legacy shaka flow always
        // used) — never call the automated-approval callable for this.
        // That callable only re-confirms the three files exist in Storage,
        // not that they show three actually-validated poses; a fallback
        // submission is, by definition, one photo (no pose was ever
        // confirmed) copied into all three slots, so "files exist" would
        // rubber-stamp it without anyone — human or machine — ever having
        // looked at it.
        setLocalVibeSubmitted(true);
        setVibeMessage(t('verification.submitted'));
        return;
      }

      // Instant grant — but the client can never set isVibeVerified/
      // faceVerificationStatus=='approved' directly (firestore.rules
      // forbids it, see isOwnFaceVerificationSubmissionUpdate). Only this
      // callable — which independently re-confirms all three uploaded
      // files actually exist before granting anything — may do that. Only
      // reached for a genuine three-pose auto-capture (isFallback === false).
      const submitFn = httpsCallable<{ requestId: string }, { success: boolean }>(
        functionsEu,
        'submitAutomatedFaceVerificationEu',
      );
      const response = await submitFn({ requestId: requestRef.id });
      if (!response.data?.success) {
        throw new Error(t('verification.submitFailed'));
      }

      setLocalVibeSubmitted(true);
      setVibeMessage(t('verification.camera.verificationComplete'));
    } catch (error) {
      // Keep the local captures around so the user can retry the
      // upload+grant step without repeating all three poses (section 17 —
      // do not force a full re-capture just because the network hiccuped).
      setVibeFailedCaptures({ front, left, right, isFallback });
      // Log the real error (technical detail, e.g. an image-load/upload
      // failure) for debugging, but never surface error.message directly to
      // the user — it can contain raw internal URIs/technical wording
      // (confirmed live: a URI-handling bug once surfaced literally as
      // "Failed to load image at file://blob:https://…" in this exact
      // message). Always show the existing, translated, user-safe copy.
      console.warn('[Tonyte][Verification] submitVibeVerification failed', error);
      setVibeMessage(t('verification.submitFailed'));
    } finally {
      setVibeSaving(false);
    }
  };

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <KeyboardAvoidingView
        style={[styles.keyboardBody, Platform.OS === 'android' && { paddingBottom: androidKeyboard.height }]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + Spacing.sm, paddingBottom: (androidKeyboard.visible ? 0 : insets.bottom) + Spacing.xl }]}
      >
        <View style={styles.header}>
          <TouchableOpacity style={[styles.backBtn, appearance.backBtn]} onPress={() => navigation.goBack()}>
            <Icon name="arrow-left" size={20} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, appearance.textPrimary]}>{t('verification.title')}</Text>
          <View style={{ width: 36 }} />
        </View>

        <Text style={[styles.intro, appearance.textSecondary]}>{t('verification.subtitle')}</Text>

        <VerificationSection
          icon="user-check"
          title={t('verification.vibe')}
          status={vibeUi}
          description={t('verification.vibeDescription')}
          buttonLabel={vibeUi.tone === 'success' ? t('verification.verified') : vibeUi.tone === 'pending' ? t('verification.pendingReview') : vibeUi.tone === 'failed' ? t('verification.tryAgain') : t('verification.start')}
          disabled={vibeUi.tone === 'success' || vibeUi.tone === 'pending' || vibeSaving}
          loading={vibeSaving}
          onPress={() => setPoseGuideVisible(true)}
        >
          {vibeFailedCaptures ? (
            <View style={styles.reviewBlock}>
              <View style={styles.buttonRow}>
                <TouchableOpacity style={[styles.secondaryButton, appearance.secondaryButton]} onPress={openLiveVerificationCamera} disabled={vibeSaving}>
                  <Text style={[styles.secondaryButtonText, appearance.textPrimary]}>{t('verification.retake')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={() => void submitVibeVerification(vibeFailedCaptures.front, vibeFailedCaptures.left, vibeFailedCaptures.right, vibeFailedCaptures.isFallback)}
                  disabled={vibeSaving}
                >
                  <Text style={styles.primaryButtonText}>{vibeSaving ? t('verification.submitting') : t('verification.camera.tryAgain')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : null}
          {vibeMessage ? <Text style={localVibeSubmitted ? styles.message : styles.errorMessage}>{vibeMessage}</Text> : null}
        </VerificationSection>

        <VerificationSection
          icon="award"
          title={t('verification.student')}
          status={studentUi}
          description={t('verification.studentDescription')}
          // TEMPORARY: pending state normally hides the input/button entirely
          // (see disabled= below) to stop duplicate submissions while a
          // request is under review. Unlocked here for a one-off manual
          // resend while debugging the email-delivery path — revert the
          // `tone === 'pending'` exceptions below once no longer needed.
          buttonLabel={studentUi.tone === 'success' ? t('verification.verified') : studentUi.tone === 'pending' ? 'Resend verification email' : studentUi.tone === 'failed' ? t('verification.tryAgain') : t('studiZone.verifyStatus')}
          disabled={studentUi.tone === 'success' || studentSaving}
          loading={studentSaving}
          onPress={sendStudentVerification}
        >
          {studentUi.tone !== 'success' ? (
            <TextInput
              value={studentEmail}
              onChangeText={setStudentEmail}
              placeholder={t('verification.emailPlaceholder')}
              placeholderTextColor={theme.colors.textMuted}
              autoCapitalize="none"
              keyboardType="email-address"
              returnKeyType="send"
              onSubmitEditing={sendStudentVerification}
              onFocus={() => { setEmailFocused(true); revealEmailField(); }}
              onBlur={() => setEmailFocused(false)}
              style={[styles.input, appearance.input]}
            />
          ) : null}
          {studentMessage ? <Text style={studentMessage === t('verification.emailSent') ? styles.message : styles.errorMessage}>{studentMessage}</Text> : null}
        </VerificationSection>
      </ScrollView>
      </KeyboardAvoidingView>

      <Modal visible={poseGuideVisible} transparent animationType="fade" onRequestClose={() => setPoseGuideVisible(false)}>
        <View style={styles.poseGuideOverlay}>
          <View style={[styles.poseGuideCard, appearance.card, styles.poseGuideCardOpaque]}>
            <ScrollView
              style={styles.poseGuideScroll}
              contentContainerStyle={styles.poseGuideScrollContent}
              showsVerticalScrollIndicator={false}
            >
              <Text style={[styles.poseGuideTitle, appearance.textPrimary]}>{t('verification.poseGuideTitle')}</Text>
              <Text style={[styles.poseGuideBody, appearance.textSecondary]}>{t('verification.poseGuideBody')}</Text>
            </ScrollView>
            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[styles.secondaryButton, appearance.secondaryButton]}
                onPress={() => setPoseGuideVisible(false)}
              >
                <Text style={[styles.secondaryButtonText, appearance.textPrimary]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => {
                  setPoseGuideVisible(false);
                  openLiveVerificationCamera();
                }}
              >
                <Text style={styles.primaryButtonText}>{t('verification.poseGuideOpenCamera')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <VibeVerificationCameraModal
        visible={liveCameraVisible}
        onClose={() => setLiveCameraVisible(false)}
        onCaptured={handleVibeVerificationCaptured}
      />
    </TonyteBackground>
  );
};

const VerificationSection: React.FC<{
  icon: string;
  title: string;
  status: StatusUi;
  description: string;
  buttonLabel: string;
  disabled?: boolean;
  loading?: boolean;
  onPress: () => void;
  children?: React.ReactNode;
}> = ({ icon, title, status, description, buttonLabel, disabled, loading, onPress, children }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
  <View style={[styles.card, appearance.card]}>
    <View style={styles.cardHeader}>
      <View style={styles.cardIcon}>
        <Icon name={icon as any} size={20} color="#fff" />
      </View>
      <View style={styles.cardTitleBlock}>
        <Text style={[styles.cardTitle, appearance.textPrimary]}>{title}</Text>
        <View style={[styles.statusPill, { borderColor: statusToneColor[status.tone] }]}>
          <View style={[styles.statusDot, { backgroundColor: statusToneColor[status.tone] }]} />
          <LocalizedStatusLabel status={status} />
        </View>
      </View>
    </View>
    <Text style={[styles.description, appearance.textSecondary]}>{description}</Text>
    {children}
    <TouchableOpacity style={[styles.primaryButton, disabled && styles.primaryButtonDisabled, disabled && appearance.primaryButtonDisabled]} onPress={onPress} disabled={disabled} activeOpacity={0.82}>
      {loading ? <ActivityIndicator color="#07101d" /> : <Text style={styles.primaryButtonText}>{buttonLabel}</Text>}
    </TouchableOpacity>
  </View>
  );
};

const LocalizedStatusLabel: React.FC<{ status: StatusUi }> = ({ status }) => {
  const { t } = useI18n();
  return <Text style={[styles.statusText, { color: statusToneColor[status.tone] }]}>{t(status.labelKey)}</Text>;
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  keyboardBody: { flex: 1 },
  content: { paddingHorizontal: Spacing.base, gap: Spacing.base },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: Spacing.sm },
  backBtn: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center' },
  headerTitle: { color: Colors.textPrimary, fontSize: Typography.lg, fontWeight: Typography.bold },
  intro: { color: Colors.textSecondary, fontSize: Typography.base, lineHeight: 22, marginBottom: Spacing.sm },
  card: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(4,8,15,0.76)',
    padding: Spacing.base,
    overflow: 'hidden',
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: Spacing.md },
  cardIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(242,125,38,0.18)',
  },
  cardTitleBlock: { flex: 1, gap: 8 },
  cardTitle: { color: Colors.textPrimary, fontSize: Typography.md, fontWeight: Typography.bold },
  statusPill: { alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: { fontSize: Typography.xs, fontWeight: Typography.bold },
  description: { color: Colors.textSecondary, fontSize: Typography.sm, lineHeight: 20, marginTop: Spacing.md, marginBottom: Spacing.md },
  input: {
    color: Colors.textPrimary,
    borderRadius: Radius.md,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: Spacing.md,
    paddingVertical: 12,
    marginBottom: Spacing.md,
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: Radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.turquoise,
    paddingHorizontal: Spacing.base,
  },
  primaryButtonDisabled: { backgroundColor: 'rgba(255,255,255,0.12)' },
  primaryButtonText: { color: '#07101d', fontWeight: Typography.bold, fontSize: Typography.sm },
  secondaryButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: Radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Colors.border,
  },
  secondaryButtonText: { color: Colors.textPrimary, fontWeight: Typography.bold, fontSize: Typography.sm },
  reviewBlock: { gap: Spacing.md, marginBottom: Spacing.md },
  selfie: { width: '100%', aspectRatio: 1, borderRadius: Radius.lg, backgroundColor: Colors.bgCard },
  buttonRow: { flexDirection: 'row', gap: Spacing.sm },
  message: { color: Colors.turquoise, fontSize: Typography.sm, marginBottom: Spacing.md },
  errorMessage: { color: Colors.sunset, fontSize: Typography.sm, marginBottom: Spacing.md },
  poseGuideOverlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.lg,
    // Was rgba(0,0,0,0.72) — against this app's own near-black background,
    // a black overlay at that alpha barely dims anything, so bright/white
    // text from the screen behind stayed fully legible and visually
    // collided with this modal's own (longer, since the shaka reference
    // image was replaced with multi-line instructions) content. Bumped
    // opacity so the backdrop actually reads as dimmed regardless of how
    // dark the underlying screen already is.
    backgroundColor: 'rgba(0,0,0,0.92)',
  },
  poseGuideCard: {
    width: '100%',
    maxWidth: 380,
    maxHeight: '85%',
    borderRadius: Radius.lg,
    padding: Spacing.lg,
    gap: Spacing.md,
    // Clip children to the card's rounded bounds — without this, content
    // taller than the card (long instructions text) could render past its
    // edges instead of scrolling inside it.
    overflow: 'hidden',
  },
  // Applied after `appearance.card` (array styles: later wins) so this
  // modal's background is always fully opaque regardless of what the
  // theme's shared card background resolves to — this card sits directly
  // over the rest of the app's own UI, unlike the inline section cards
  // that use `appearance.card` against a plain background.
  poseGuideCardOpaque: { backgroundColor: 'rgba(6,10,18,0.98)' },
  poseGuideScroll: { flexShrink: 1 },
  poseGuideScrollContent: { gap: Spacing.sm },
  // color here is the canonical Midnight (dark, default theme) value —
  // `appearance.textPrimary`/`textSecondary` (daylightStyle) resolve to {}
  // outside the daylight theme by design, so without a base color these
  // rendered as RN's default black text on this modal's dark card,
  // invisible. Pre-existing gap, only became visually obvious once the
  // body text got longer and the shaka reference image (which used to
  // dominate the card) was removed.
  poseGuideTitle: { fontSize: Typography.lg, fontWeight: Typography.bold, color: Colors.textPrimary },
  poseGuideBody: { fontSize: Typography.sm, lineHeight: 20, color: Colors.textSecondary },
  poseGuideImage: {
    width: '100%',
    height: 260,
    borderRadius: Radius.lg,
    backgroundColor: Colors.bgCard,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  backBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
    borderWidth: 1,
    borderRadius: 18,
    ...theme.shadows.card,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    color: theme.colors.textPrimary,
  }),
  secondaryButton: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  primaryButtonDisabled: daylightStyle(theme, {
    backgroundColor: theme.colors.borderStrong,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
});

export default VerificationCenterScreen;
